import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useTheme } from '@mui/material/styles';
import { IN_APP_FILE_PREVIEW_EVENT } from '../services/nativeFileService';

const MIME_BY_EXTENSION = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  csv: 'text/csv',
  xml: 'application/xml',
  json: 'application/json'
};

const inferMimeType = (fileName, mimeType) => {
  const normalizedMime = String(mimeType || '').split(';')[0].trim().toLowerCase();
  if (normalizedMime && normalizedMime !== 'application/octet-stream') return normalizedMime;
  const extension = String(fileName || '').split('.').pop()?.toLowerCase();
  return MIME_BY_EXTENSION[extension] || normalizedMime || 'application/octet-stream';
};

const isTextPreview = (mimeType) => (
  mimeType.startsWith('text/')
  || mimeType === 'application/xml'
  || mimeType === 'application/json'
);

const triggerDownload = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'document';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

function InAppFileViewer() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [preview, setPreview] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState('');

  const closePreview = useCallback(() => setPreview(null), []);

  useEffect(() => {
    const handlePreviewRequest = (event) => {
      const detail = event.detail || {};
      if (!detail.url) return;

      // Confirma al servicio que este componente mostrará el archivo.
      event.preventDefault();
      setPreview({
        url: detail.url,
        fileName: detail.fileName || 'document',
        mimeType: inferMimeType(detail.fileName, detail.mimeType),
        revokeOnClose: Boolean(detail.revokeOnClose)
      });
    };

    window.addEventListener(IN_APP_FILE_PREVIEW_EVENT, handlePreviewRequest);
    return () => window.removeEventListener(IN_APP_FILE_PREVIEW_EVENT, handlePreviewRequest);
  }, []);

  useEffect(() => {
    const currentPreview = preview;
    return () => {
      if (currentPreview?.revokeOnClose && currentPreview.url?.startsWith('blob:')) {
        window.URL.revokeObjectURL(currentPreview.url);
      }
    };
  }, [preview]);

  const previewType = useMemo(() => {
    const mimeType = preview?.mimeType || '';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.startsWith('image/')) return 'image';
    if (isTextPreview(mimeType)) return 'text';
    return 'unsupported';
  }, [preview?.mimeType]);

  useEffect(() => {
    if (!preview?.url || previewType !== 'text') {
      setTextContent('');
      setTextError('');
      setTextLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setTextLoading(true);
    setTextError('');

    fetch(preview.url, { credentials: 'include', signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`No se pudo cargar el archivo (${response.status}).`);
        return response.text();
      })
      .then((content) => setTextContent(content))
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setTextError(error.message || 'No se pudo mostrar el contenido del archivo.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setTextLoading(false);
      });

    return () => controller.abort();
  }, [preview?.url, previewType]);

  const downloadFile = useCallback(() => {
    if (preview?.url) triggerDownload(preview.url, preview.fileName);
  }, [preview]);

  return (
    <Dialog
      open={Boolean(preview)}
      onClose={closePreview}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xl"
      aria-labelledby="file-preview-title"
      PaperProps={{
        sx: {
          height: fullScreen ? '100dvh' : 'min(88dvh, 920px)',
          maxHeight: fullScreen ? '100dvh' : '88dvh',
          overflow: 'hidden',
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle id="file-preview-title" sx={{ pr: 7, py: 1.75 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
          <InsertDriveFileOutlinedIcon color="primary" aria-hidden="true" />
          <Typography
            variant="h6"
            component="span"
            sx={{ minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.25 }}
          >
            {preview?.fileName || 'Vista previa del documento'}
          </Typography>
        </Stack>
        <IconButton
          aria-label="Cerrar vista previa"
          onClick={closePreview}
          sx={{ position: 'absolute', right: 8, top: 8, width: 44, height: 44 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: previewType === 'image' ? { xs: 1, sm: 2 } : 0,
          minHeight: 0,
          overflow: 'auto',
          bgcolor: previewType === 'image' ? 'action.hover' : 'background.default'
        }}
      >
        {previewType === 'pdf' && preview?.url && (
          <Box
            component="iframe"
            src={preview.url}
            title={`Vista previa de ${preview.fileName}`}
            sx={{ display: 'block', width: '100%', height: '100%', minHeight: '65dvh', border: 0 }}
          />
        )}

        {previewType === 'image' && preview?.url && (
          <Box
            component="img"
            src={preview.url}
            alt={`Vista previa de ${preview.fileName}`}
            sx={{ display: 'block', maxWidth: '100%', maxHeight: '100%', mx: 'auto', objectFit: 'contain' }}
          />
        )}

        {previewType === 'text' && (
          <Box sx={{ minHeight: '100%', p: { xs: 2, sm: 3 } }}>
            {textLoading && (
              <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }} spacing={1.5}>
                <CircularProgress size={30} />
                <Typography color="text.secondary">Cargando archivo…</Typography>
              </Stack>
            )}
            {textError && <Alert severity="error">{textError}</Alert>}
            {!textLoading && !textError && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  color: 'text.primary',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.65
                }}
              >
                {textContent}
              </Box>
            )}
          </Box>
        )}

        {previewType === 'unsupported' && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ minHeight: '100%', p: 3, textAlign: 'center' }}
          >
            <InsertDriveFileOutlinedIcon color="primary" sx={{ fontSize: 64 }} aria-hidden="true" />
            <Box>
              <Typography variant="h6" gutterBottom>Este formato no admite vista previa</Typography>
              <Typography color="text.secondary">
                Descarga el archivo para abrirlo con la aplicación correspondiente.
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
        <Button onClick={closePreview} color="inherit">Cerrar</Button>
        <Button onClick={downloadFile} variant="contained" startIcon={<DownloadIcon />}>
          Descargar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InAppFileViewer;
